import React, { useEffect, useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent } from '../components/Card';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Textarea } from '../components/Textarea';
import { Label } from '../components/Label';
import { Bot, AlertTriangle, Activity, ShieldAlert, FileText, MapPin, Wand2, Save, RotateCcw, Sparkles, Plus, X, Cpu, Lock, Eye } from 'lucide-react';
import { toast } from 'sonner';
type ServiceId = 'hot-issue' | 'sentiment' | 'threat-intel' | 'daily-brief' | 'visit-intel' | 'deepfake';
type SubTabId = 'generate' | 'scoring' | 'threshold' | 'keywords';
type ServiceConfig = {
  id: ServiceId;
  label: string;
  shortLabel: string;
  icon: any;
  description: string;
  generateSystem: string;
  generateTemplate: string;
  scoringRule: string;
  thresholdRule: string;
  keywords: string[];
  subTabs: SubTabId[];
};
const SERVICES: ServiceConfig[] = [{
  id: 'hot-issue',
  label: 'Hot Issue & Crisis Early Warning',
  shortLabel: 'Hot Issue',
  icon: AlertTriangle,
  description: 'Konfigurasi deteksi isu panas, scoring eskalasi, dan threshold krisis.',
  generateSystem: 'Anda adalah analis intelijen senior untuk Pasukan Pengamanan Presiden Republik Indonesia. Tugas Anda mengidentifikasi isu yang berpotensi naik menjadi krisis dalam 24 jam berdasarkan lonjakan percakapan, polarisasi sentimen, dan aktor amplifikasi. Output harus akurat, factual, dan disertai confidence score.',
  generateTemplate: 'Topik: {topic}\nWilayah: {region}\nRentang waktu: {time_range}\nSumber data: {data_sources}\nKonteks tambahan: {additional_context}',
  scoringRule: 'Hitung skor eskalasi dari kombinasi: (a) volume mention growth %, (b) sentiment polarization index, (c) actor influence score, (d) coordination index bot-like, (e) media amplification factor. Normalisasi 0-100.',
  thresholdRule: 'Hijau: skor 0-39 (monitoring saja)\nKuning: skor 40-59 (isu meningkat)\nOranye: skor 60-79 (berpotensi krisis)\nMerah: skor 80-94 (krisis aktif)\nHitam: skor 95-100 (ancaman keamanan serius)',
  keywords: ['Presiden', 'Bansos', 'Pangan', 'BBM', 'Pajak', 'Demo', 'Boikot', 'Hashtag viral'],
  subTabs: ['generate', 'scoring', 'threshold', 'keywords']
}, {
  id: 'sentiment',
  label: 'Presidential Sentiment Monitoring',
  shortLabel: 'Sentiment',
  icon: Activity,
  description: 'Konfigurasi analisis sentimen rakyat terhadap Presiden, program, dan kebijakan.',
  generateSystem: 'Anda adalah AI analyst yang menilai sentimen publik Indonesia terhadap Presiden RI dan program pemerintah. Sentimen harus diklasifikasi: positif, netral, negatif, marah, kecewa, takut, mendukung, curiga, provokatif, sarkastik. Sertakan justifikasi dan kutipan representatif.',
  generateTemplate: 'Subjek: {subject}\nProgram terkait: {program}\nWilayah: {region}\nRentang waktu: {time_range}\nPlatform: {platforms}',
  scoringRule: 'Hitung skor agregat sentimen dengan bobot: positif (+1), mendukung (+1.2), netral (0), curiga (-0.3), kecewa (-0.7), negatif (-1), marah (-1.2), provokatif (-1.5). Normalisasi ke 0-100 (50 = balanced).',
  thresholdRule: '',
  keywords: ['Presiden RI', 'Wakil Presiden', 'Program MBG', 'Swasembada', 'Hilirisasi', 'IKN', 'Kebijakan'],
  subTabs: ['generate', 'scoring', 'keywords']
}, {
  id: 'threat-intel',
  label: 'Threat Intelligence & Protective Alert',
  shortLabel: 'Threat Intel',
  icon: ShieldAlert,
  description: 'Konfigurasi deteksi ancaman keamanan Presiden, deepfake, dan kebocoran jadwal.',
  generateSystem: 'Anda adalah analis ancaman digital untuk pengamanan Presiden. Deteksi ancaman eksplisit, ajakan kekerasan, kebocoran jadwal, akun palsu, phishing mengatasnamakan Presiden, dan kampanye delegitimasi. Setiap ancaman harus disertai severity score, evidence link, dan rekomendasi eskalasi.',
  generateTemplate: 'Tipe ancaman: {threat_type}\nTarget: {target}\nLokasi/agenda: {context}\nRentang waktu: {time_range}',
  scoringRule: 'Severity score = (specificity × 0.3) + (capability × 0.3) + (intent × 0.25) + (reach × 0.15). Skala 0-100. Skor ≥ 80 wajib dieskalasi dalam 1 jam.',
  thresholdRule: 'Severity 0-39: monitoring\n40-59: alert internal\n60-79: koordinasi unit terkait\n80-94: eskalasi Paspampres + BSSN\n95-100: ancaman keamanan serius — eskalasi POLRI Cybercrime',
  keywords: ['Ancaman Presiden', 'Deepfake', 'Phishing presidenri', 'Lokasi kunjungan', 'Jadwal sensitif', 'Akun palsu'],
  subTabs: ['generate', 'scoring', 'threshold', 'keywords']
}, {
  id: 'daily-brief',
  label: 'Daily Presidential Intelligence Brief',
  shortLabel: 'Daily Brief',
  icon: FileText,
  description: 'Konfigurasi pembuatan briefing harian otomatis untuk Presiden dan pimpinan.',
  generateSystem: 'Anda adalah AI penyusun briefing harian untuk Presiden RI. Output harus terstruktur: (1) Top 10 isu nasional, (2) Top 10 isu menyebut Presiden, (3) Isu paling berisiko, (4) Wilayah lonjakan negatif, (5) Aktor paling berpengaruh, (6) Rekomendasi tindakan. Format: 1 halaman ringkas untuk Presiden, 5 halaman teknis untuk pimpinan pengamanan.',
  generateTemplate: 'Tanggal: {date}\nPeriode: {period}\nFokus area: {focus_areas}\nLevel audiens: {audience_level}',
  scoringRule: 'Prioritisasi isu berdasarkan: (a) relevansi terhadap Presiden, (b) impact score, (c) urgency 24-jam, (d) coverage media nasional. Top 10 = skor tertinggi.',
  thresholdRule: '',
  keywords: ['Presiden RI', 'Kebijakan strategis', 'Kunjungan', 'Krisis nasional', 'Pernyataan resmi'],
  subTabs: ['generate', 'scoring', 'keywords']
}, {
  id: 'visit-intel',
  label: 'Presidential Visit Intelligence',
  shortLabel: 'Visit Intel',
  icon: MapPin,
  description: 'Konfigurasi brief intelijen sebelum kunjungan Presiden ke daerah.',
  generateSystem: 'Anda adalah analis kunjungan presidensial. Sebelum Presiden berkunjung ke daerah X, susun: (1) Isu lokal 30 hari, (2) Sentimen wilayah terhadap pusat, (3) Tokoh lokal vokal, (4) Komunitas masyarakat aktif, (5) Potensi demonstrasi/konflik, (6) Titik rawan narasi, (7) Rekomendasi komunikasi & pengamanan.',
  generateTemplate: 'Wilayah kunjungan: {region}\nTanggal: {visit_date}\nAgenda: {agenda}\nDurasi: {duration}\nVenue: {venue}',
  scoringRule: 'Travel risk score = (local_unrest × 0.35) + (visit_sensitivity × 0.25) + (narrative_risk × 0.2) + (schedule_exposure × 0.2). Skala 0-100.',
  thresholdRule: 'Risk 0-39: kunjungan aman\n40-59: monitoring ketat\n60-79: koordinasi penguatan\n80-94: eskalasi penuh Paspampres\n95-100: pertimbangkan penundaan/perubahan rute',
  keywords: ['Demo lokal', 'Konflik agraria', 'Tokoh oposisi daerah', 'Komunitas adat', 'Kebocoran jadwal'],
  subTabs: ['generate', 'scoring', 'threshold', 'keywords']
}, {
  id: 'deepfake',
  label: 'Deepfake & Synthetic Media Detection',
  shortLabel: 'Deepfake',
  icon: Eye,
  description: 'Konfigurasi deteksi konten sintetis (deepfake audio/video) Presiden.',
  generateSystem: 'Anda adalah AI deteksi konten sintetis. Analisis input video/audio/gambar terhadap kemungkinan manipulasi: lip-sync mismatch, facial artifact, audio synthetic pattern, document forgery. Output: confidence score manipulasi (0-100) + rekomendasi klarifikasi cepat.',
  generateTemplate: 'Tipe media: {media_type}\nSumber: {source_url}\nPlatform: {platform}\nReferensi resmi: {official_reference}',
  scoringRule: 'Manipulation confidence = average dari (lip_sync_score + face_artifact_score + audio_synthetic_score + visual_inconsistency_score). Threshold deteksi ≥ 70%.',
  thresholdRule: 'Confidence 0-49: kemungkinan otentik\n50-69: perlu verifikasi tim\n70-89: deepfake berisiko — klarifikasi resmi 2 jam\n90-100: deepfake terkonfirmasi — eskalasi takedown & legal',
  keywords: ['Suara Presiden', 'Wajah Presiden', 'Dokumen resmi palsu', 'Watermark AI', 'Lip-sync anomaly'],
  subTabs: ['generate', 'scoring', 'threshold', 'keywords']
}];
const subTabMeta: Record<SubTabId, {
  label: string;
  icon: any;
}> = {
  generate: {
    label: 'Generate Config',
    icon: Wand2
  },
  scoring: {
    label: 'Scoring Rules',
    icon: Activity
  },
  threshold: {
    label: 'Threshold Rules',
    icon: AlertTriangle
  },
  keywords: {
    label: 'Keywords',
    icon: Sparkles
  }
};
export function AiConfiguration() {
  const [activeService, setActiveService] = useState<ServiceId>('hot-issue');
  const [activeSubTab, setActiveSubTab] = useState<SubTabId>('generate');
  const [configs, setConfigs] = useState<ServiceConfig[]>(SERVICES);
  const [newKeyword, setNewKeyword] = useState('');
  const service = configs.find(s => s.id === activeService)!;
  const updateService = (patch: Partial<ServiceConfig>) => {
    setConfigs(prev => prev.map(s => s.id === activeService ? {
      ...s,
      ...patch
    } : s));
  };
  const handleSubTabChange = (id: SubTabId) => {
    if (service.subTabs.includes(id)) setActiveSubTab(id);
  };
  // Ensure subtab is valid for current service
  useEffect(() => {
    if (!service.subTabs.includes(activeSubTab)) {
      setActiveSubTab(service.subTabs[0]);
    }
  }, [activeService]);
  const handleSave = () => {
    toast.success('Konfigurasi AI tersimpan', {
      description: `${service.label} — perubahan akan berlaku pada generasi berikutnya.`
    });
  };
  const handleReset = () => {
    const original = SERVICES.find(s => s.id === activeService)!;
    setConfigs(prev => prev.map(s => s.id === activeService ? original : s));
    toast.info('Reset ke konfigurasi default');
  };
  const addKeyword = () => {
    if (!newKeyword.trim()) return;
    if (service.keywords.includes(newKeyword.trim())) {
      toast.error('Keyword sudah ada');
      return;
    }
    updateService({
      keywords: [...service.keywords, newKeyword.trim()]
    });
    setNewKeyword('');
  };
  const removeKeyword = (k: string) => {
    updateService({
      keywords: service.keywords.filter(x => x !== k)
    });
  };
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6" data-id="element-1114">
      <PageHeader group="Sistem" title="AI Configuration" description="Atur perilaku AI untuk setiap modul — system prompt, scoring, threshold, dan keyword. Perubahan tercatat di audit log." icon={Bot} data-id="element-1115" />

      {/* Role-gated notice */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3" data-id="element-1116">
        <Lock className="size-4 text-amber-700 shrink-0 mt-0.5" data-id="element-1117" />
        <div className="flex-1" data-id="element-1118">
          <p className="font-mono text-[10px] uppercase tracking-wider text-amber-700 font-bold" data-id="element-1119">
            Restricted · Cmd Center & Auditor Only
          </p>
          <p className="text-xs text-amber-900 mt-0.5 leading-relaxed" data-id="element-1120">
            Perubahan konfigurasi AI akan memengaruhi semua output Garuda Shield
            C4I. Semua aksi tercatat secara <b data-id="element-1121">immutable</b> di audit log dan
            memerlukan approval supervisor untuk produksi.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 sm:gap-6" data-id="element-1122">
        {/* Service sidebar */}
        <aside className="col-span-12 lg:col-span-4 xl:col-span-3" data-id="element-1123">
          <Card className="bg-card border-border/60 sticky top-4" data-id="element-1124">
            <CardContent className="p-0" data-id="element-1125">
              <div className="px-4 py-3 border-b border-border/60" data-id="element-1126">
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-bold flex items-center gap-1.5" data-id="element-1127">
                  <Cpu className="size-3" data-id="element-1128" /> AI Services
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5" data-id="element-1129">
                  {configs.length} modul tersedia
                </p>
              </div>
              <div className="p-1.5" data-id="element-1130">
                {configs.map(s => {
                const active = s.id === activeService;
                const Icon = s.icon;
                return <button key={s.id} onClick={() => setActiveService(s.id)} className={`w-full text-left rounded-md p-2.5 flex items-start gap-2.5 transition group ${active ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-foreground/80'}`} data-id="element-1131">
                      <div className={`size-7 rounded-md flex items-center justify-center shrink-0 ${active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:bg-card'}`} data-id="element-1132">
                        <Icon className="size-3.5" data-id="element-1133" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1134">
                        <p className={`text-xs font-semibold truncate ${active ? 'text-primary' : ''}`} data-id="element-1135">
                          {s.shortLabel}
                        </p>
                        <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5 truncate" data-id="element-1136">
                          {s.keywords.length} keywords · {s.subTabs.length} tabs
                        </p>
                      </div>
                    </button>;
              })}
              </div>
            </CardContent>
          </Card>
        </aside>

        {/* Editor */}
        <section className="col-span-12 lg:col-span-8 xl:col-span-9" data-id="element-1137">
          <Card className="bg-card border-border/60" data-id="element-1138">
            <CardContent className="p-0" data-id="element-1139">
              {/* Service header */}
              <div className="px-5 py-4 border-b border-border/60 flex items-start justify-between gap-3 flex-wrap" data-id="element-1140">
                <div className="flex items-start gap-3 min-w-0" data-id="element-1141">
                  <div className="size-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0" data-id="element-1142">
                    <service.icon className="size-5 text-primary" data-id="element-1143" />
                  </div>
                  <div className="min-w-0" data-id="element-1144">
                    <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-1145">
                      <Badge variant="outline" className="font-mono text-[9px] h-4 bg-emerald-50 text-emerald-700 border-emerald-200" data-id="element-1146">
                        ACTIVE
                      </Badge>
                      <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground" data-id="element-1147">
                        v4.1 · Garuda-LLM
                      </span>
                    </div>
                    <h2 className="font-heading text-base sm:text-lg font-bold tracking-tight" data-id="element-1148">
                      {service.label}
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl" data-id="element-1149">
                      {service.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0" data-id="element-1150">
                  <Button variant="outline" size="sm" onClick={handleReset} data-id="element-1151">
                    <RotateCcw className="size-3.5 mr-1.5" data-id="element-1152" />
                    Reset
                  </Button>
                  <Button size="sm" onClick={handleSave} data-id="element-1153">
                    <Save className="size-3.5 mr-1.5" data-id="element-1154" />
                    Save Settings
                  </Button>
                </div>
              </div>

              {/* Sub-tabs */}
              <div className="px-5 pt-4" data-id="element-1155">
                <div className="rounded-md bg-muted/40 border border-border/60 p-1 inline-flex gap-1 flex-wrap" data-id="element-1156">
                  {service.subTabs.map(t => {
                  const meta = subTabMeta[t];
                  const active = activeSubTab === t;
                  return <button key={t} onClick={() => handleSubTabChange(t)} className={`px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-wider transition flex items-center gap-1.5 ${active ? 'bg-card text-foreground shadow-sm border border-border/60 font-bold' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-1157">
                        <meta.icon className="size-3" data-id="element-1158" />
                        {meta.label}
                      </button>;
                })}
                </div>
              </div>

              {/* Sub-tab content */}
              <div className="px-5 py-5" data-id="element-1159">
                {activeSubTab === 'generate' && <div className="space-y-4" data-id="element-1160">
                    <div data-id="element-1161">
                      <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-bold" data-id="element-1162">
                        System Prompt
                      </Label>
                      <p className="text-[11px] text-muted-foreground mt-0.5 mb-1.5" data-id="element-1163">
                        Instruksi inti untuk AI saat menghasilkan output modul
                        ini.
                      </p>
                      <Textarea value={service.generateSystem} onChange={e => updateService({
                    generateSystem: e.target.value
                  })} className="min-h-[140px] font-mono text-xs bg-muted/30 border-border/60 leading-relaxed" data-id="element-1164" />
                    </div>
                    <div data-id="element-1165">
                      <div className="flex items-center justify-between mb-1.5" data-id="element-1166">
                        <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-bold" data-id="element-1167">
                          Context Template
                        </Label>
                        <span className="font-mono text-[9px] text-muted-foreground" data-id="element-1168">
                          Gunakan{' '}
                          <code className="bg-muted px-1 rounded" data-id="element-1169">
                            {'{variable}'}
                          </code>
                        </span>
                      </div>
                      <Textarea value={service.generateTemplate} onChange={e => updateService({
                    generateTemplate: e.target.value
                  })} className="min-h-[120px] font-mono text-xs bg-muted/30 border-border/60 leading-relaxed" data-id="element-1170" />
                    </div>
                  </div>}

                {activeSubTab === 'scoring' && <div data-id="element-1171">
                    <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-bold" data-id="element-1172">
                      Scoring Rule
                    </Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5 mb-1.5" data-id="element-1173">
                      Formula dan bobot yang digunakan untuk menghitung skor
                      output AI.
                    </p>
                    <Textarea value={service.scoringRule} onChange={e => updateService({
                  scoringRule: e.target.value
                })} className="min-h-[180px] font-mono text-xs bg-muted/30 border-border/60 leading-relaxed" data-id="element-1174" />
                  </div>}

                {activeSubTab === 'threshold' && <div data-id="element-1175">
                    <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-bold" data-id="element-1176">
                      Threshold Rules
                    </Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5 mb-1.5" data-id="element-1177">
                      Batasan skor yang memicu klasifikasi level (Hijau / Kuning
                      / Oranye / Merah / Hitam).
                    </p>
                    <Textarea value={service.thresholdRule} onChange={e => updateService({
                  thresholdRule: e.target.value
                })} className="min-h-[180px] font-mono text-xs bg-muted/30 border-border/60 leading-relaxed" data-id="element-1178" />
                  </div>}

                {activeSubTab === 'keywords' && <div data-id="element-1179">
                    <div className="flex items-center justify-between mb-2" data-id="element-1180">
                      <div data-id="element-1181">
                        <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-bold" data-id="element-1182">
                          Keywords ({service.keywords.length})
                        </Label>
                        <p className="text-[11px] text-muted-foreground mt-0.5" data-id="element-1183">
                          Kata kunci yang diprioritaskan AI saat memproses data.
                        </p>
                      </div>
                    </div>

                    {/* Add new */}
                    <div className="flex gap-2 mb-3" data-id="element-1184">
                      <Input value={newKeyword} onChange={e => setNewKeyword(e.target.value)} onKeyDown={e => e.key === 'Enter' && addKeyword()} placeholder="Tambah keyword baru..." className="bg-muted/30 border-border/60" data-id="element-1185" />
                      <Button onClick={addKeyword} data-id="element-1186">
                        <Plus className="size-3.5 mr-1.5" data-id="element-1187" />
                        Add Keyword
                      </Button>
                    </div>

                    {/* List */}
                    <div className="rounded-lg border border-border/60 bg-card overflow-hidden" data-id="element-1188">
                      {service.keywords.length === 0 ? <div className="text-center py-8 px-4" data-id="element-1189">
                          <Sparkles className="size-8 text-muted-foreground/40 mx-auto mb-2" data-id="element-1190" />
                          <p className="text-sm text-muted-foreground" data-id="element-1191">
                            Belum ada keyword
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-1" data-id="element-1192">
                            Tambahkan keyword pertama di atas
                          </p>
                        </div> : <div className="divide-y divide-border/60" data-id="element-1193">
                          {service.keywords.map((k, i) => <div key={k} className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/30 transition group" data-id="element-1194">
                              <span className="font-mono text-[10px] text-muted-foreground w-6 shrink-0" data-id="element-1195">
                                {String(i + 1).padStart(2, '0')}
                              </span>
                              <Input value={k} onChange={e => {
                        const next = [...service.keywords];
                        next[i] = e.target.value;
                        updateService({
                          keywords: next
                        });
                      }} className="flex-1 bg-transparent border-0 h-7 px-1 focus-visible:ring-1" data-id="element-1196" />
                              <button onClick={() => removeKeyword(k)} className="size-7 rounded-md hover:bg-red-50 hover:text-red-600 text-muted-foreground flex items-center justify-center transition opacity-0 group-hover:opacity-100" aria-label={`Hapus keyword ${k}`} data-id="element-1197">
                                <X className="size-3.5" data-id="element-1198" />
                              </button>
                            </div>)}
                        </div>}
                    </div>
                  </div>}
              </div>

              {/* Footer metadata */}
              <div className="border-t border-border/60 px-5 py-3 bg-muted/30 flex items-center justify-between flex-wrap gap-2" data-id="element-1199">
                <div className="flex items-center gap-3 font-mono text-[10px] text-muted-foreground" data-id="element-1200">
                  <span className="flex items-center gap-1" data-id="element-1201">
                    <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-1202" />
                    Live config
                  </span>
                  <span data-id="element-1203">·</span>
                  <span data-id="element-1204">Last edited 5 menit lalu by CC-03</span>
                  <span data-id="element-1205">·</span>
                  <span data-id="element-1206">Audit ID: AI-CFG-2026-0142</span>
                </div>
                <span className="font-mono text-[10px] text-muted-foreground" data-id="element-1207">
                  Model: <b className="text-foreground" data-id="element-1208">Garuda-LLM v4.1</b>
                </span>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>;
}